import { useEffect, useState } from "react";
import { Loader2, Save, UserCog } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export default function DriverEditor({ driver, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", truck_number: "", hourly_rate: "", password: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (driver) {
      setForm({
        name: driver.name || "",
        email: driver.email || "",
        truck_number: driver.truck_number || "",
        hourly_rate: driver.hourly_rate != null ? String(driver.hourly_rate) : "",
        password: "",
      });
    }
  }, [driver]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        truck_number: form.truck_number,
        hourly_rate: parseFloat(form.hourly_rate) || 0,
      };
      if (form.password.trim()) payload.password = form.password.trim();
      await api.patch(`/admin/drivers/${driver.id}`, payload);
      toast.success("Profil du chauffeur mis à jour");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#131C2E] border-[#1E293B] text-white">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <UserCog className="w-5 h-5 text-sky-400" /> Modifier le profil
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Modifiez les informations du chauffeur. Laissez le mot de passe vide pour le conserver.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="driver-edit-form">
          <div>
            <Label className="text-slate-300">Nom complet</Label>
            <Input required value={form.name} data-testid="edit-driver-name-input" onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-[#0B0F17] border-[#1E293B]" />
          </div>
          <div>
            <Label className="text-slate-300">Adresse courriel</Label>
            <Input required type="email" value={form.email} data-testid="edit-driver-email-input" onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-[#0B0F17] border-[#1E293B]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300">Camion #</Label>
              <Input value={form.truck_number} data-testid="edit-driver-truck-input" onChange={(e) => setForm({ ...form, truck_number: e.target.value })} className="mt-1 bg-[#0B0F17] border-[#1E293B]" />
            </div>
            <div>
              <Label className="text-slate-300">Taux horaire ($)</Label>
              <Input type="number" step="0.01" value={form.hourly_rate} data-testid="edit-driver-rate-input" onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} className="mt-1 bg-[#0B0F17] border-[#1E293B]" />
            </div>
          </div>
          <div>
            <Label className="text-slate-300">Nouveau mot de passe <span className="text-slate-500 text-xs">(optionnel)</span></Label>
            <Input type="text" value={form.password} data-testid="edit-driver-password-input" onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Laisser vide pour ne pas changer" className="mt-1 bg-[#0B0F17] border-[#1E293B]" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="edit-driver-submit" className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-[#0B0F17] font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Enregistrer</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
